import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SocialButtons from "@/components/SocialButtons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/BrandMark";
import AuthLegalFooter from "@/components/legal/AuthLegalFooter";
import { track, EVENTS } from "@/lib/analytics";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const paidPlan = params.get("plan");
  const paidPeriod = params.get("period");
  const paidCurrency = params.get("currency");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { verificationRequired } = await login(email, password);
      // Route unverified users to the "check your email" screen; backend
      // enforcement still applies via 403 EMAIL_NOT_VERIFIED on protected APIs.
      if (verificationRequired) {
        toast.info("Verify your email to unlock Career OS.");
        nav("/verify-email", { replace: true });
        return;
      }
      track(EVENTS.LOGIN_COMPLETED);
      toast.success("Welcome back");
      // Preserve paid-plan intent through login → /app/pricing for confirmation.
      if (paidPlan && ["pro", "premium"].includes(paidPlan)) {
        const q = new URLSearchParams({ plan: paidPlan });
        if (paidPeriod && ["monthly", "yearly"].includes(paidPeriod)) q.set("period", paidPeriod);
        if (paidCurrency && /^[A-Z]{3}$/i.test(paidCurrency)) q.set("currency", paidCurrency.toUpperCase());
        nav(`/app/pricing?${q.toString()}`);
      } else {
        nav("/app");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen flex flex-col bg-background text-foreground" data-testid="login-page">
      <div className="flex flex-1 min-h-0">
      <div className="hidden md:flex flex-1 bg-card p-10 flex-col justify-between border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-30 pointer-events-none" />
        <Link to="/" className="flex items-center gap-2 relative">
          <BrandMark size={32} className="rounded-lg" />
          <span className="font-display font-bold tracking-tight text-lg">Career OS</span>
        </Link>
        <div className="relative">
          <div className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Welcome back</div>
          <div className="font-display font-black text-4xl tracking-tight leading-tight mb-4">
            Your next $50K is one login away.
          </div>
          <div className="text-sm text-muted-foreground max-w-md leading-relaxed">
            Your Career OS remembers where you left off — resume score, roadmap, salary intel, and pending follow-ups.
          </div>
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground/60">Career OS v2.0</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="font-display font-black text-3xl tracking-tight">Log in</div>
            <div className="text-sm text-muted-foreground mt-2">
              New here?{" "}
              <Link
                to={`/signup${paidPlan && ["pro", "premium"].includes(paidPlan)
                  ? `?plan=${paidPlan}${paidPeriod && ["monthly", "yearly"].includes(paidPeriod) ? `&period=${paidPeriod}` : ""}${paidCurrency && /^[A-Z]{3}$/i.test(paidCurrency) ? `&currency=${paidCurrency.toUpperCase()}` : ""}`
                  : ""}`}
                className="text-primary hover:underline font-medium"
                data-testid="link-signup"
              >
                Create an account
              </Link>
            </div>
          </div>
          <div>
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              data-testid="login-email"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              data-testid="login-password"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5"
            />
          </div>
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full h-11 text-base"
          >
            {loading ? "Signing in…" : "Log in"}
          </Button>
          <div className="text-center text-xs">
            <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-password-link">Forgot your password?</Link>
          </div>
          <SocialButtons testIdPrefix="login-" />
        </form>
      </div>
      </div>
      <AuthLegalFooter />
    </div>
  );
}
